import moment from "moment";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type {
  IDayTimeAttendanceByEmpId,
  IDayTimeAttendanceEmpClockIn,
  TDayTimeAttendanceEmpClockOut,
  TGetCheckedHistory,
  IGetTimeAttendanceReportData,
  IGetTimeAttendanceSummaryData,
  ILatestCheckedTime,
} from "../../types/(my-space)/timeAttendace.interface";
import { getCurrentDateYMDformat, getTimeDifference, getTimeDifferenceInMS } from "../../utils/helper";

export interface TimeAttendanceState {
  timeAttendanceByEmployeeId: IDayTimeAttendanceByEmpId | null;
  clockIn: IDayTimeAttendanceEmpClockIn | null;
  clockOut: TDayTimeAttendanceEmpClockOut | null;
  lastCheckedTime: ILatestCheckedTime | null;
  userCheckinHistory: ILatestCheckedTime[] | null;
  userCheckinHistoryPage: ILatestCheckedTime[] | null;
  checkedHistoryByUserId: TGetCheckedHistory[] | null;
  totalTimePerDay: number | null;
  timeAttendanceReportData: IGetTimeAttendanceReportData | null;
  timeAttendanceSummaryData: IGetTimeAttendanceSummaryData | null;
}

const initialState: TimeAttendanceState = {
  timeAttendanceByEmployeeId: null,
  clockIn: null,
  clockOut: null,
  lastCheckedTime: null,
  userCheckinHistory: null,
  userCheckinHistoryPage: null,
  checkedHistoryByUserId: null,
  totalTimePerDay: null,
  timeAttendanceReportData: null,
  timeAttendanceSummaryData: null,
};

const timeAttendanceSlice = createSlice({
  name: "timeAttendance",
  initialState,
  reducers: {
    setTimeAttendanceSlice: (state, action: PayloadAction<Partial<TimeAttendanceState>>) => {
      const { checkedHistoryByUserId } = action.payload;

      if (checkedHistoryByUserId) {
        const totalTimePerDay = checkedHistoryByUserId.reduce((acc, cur) => {
          const data = getTimeDifferenceInMS(cur.clockInTime, cur.clockOutTime);

          if (data) return acc + data;

          return acc;
        }, 0);

        return {
          ...state,
          ...action.payload,
          totalTimePerDay,
        };
      }

      return {
        ...state,
        ...action.payload,
      };
    },

    setAddCheckinForUser: (state, action: PayloadAction<{ clockInTime: string; userId: number }>) => {
      const { clockInTime, userId } = action.payload;

      state.clockIn = {
        id: state.checkedHistoryByUserId?.length
          ? state.checkedHistoryByUserId[state.checkedHistoryByUserId.length - 1].id + 1
          : 1,
        clockInTime,
        clockOutTime: null,
        notes: null,
        createdAt: "",
        updatedAt: "",
        userId,
        date: getCurrentDateYMDformat(),
      };

      state.clockOut = null;

      // Always create a new array!
      state.checkedHistoryByUserId = [
        ...(state.checkedHistoryByUserId || []),
        {
          id:
            state.checkedHistoryByUserId && state.checkedHistoryByUserId.length
              ? state.checkedHistoryByUserId[state.checkedHistoryByUserId.length - 1].id + 1
              : 1,
          clockInTime,
          clockOutTime: null,
          notes: null,
          createdAt: "",
          updatedAt: "",
          userId,
          date: getCurrentDateYMDformat(),
        },
      ];

      state.lastCheckedTime = {
        id:
          state.checkedHistoryByUserId && state.checkedHistoryByUserId.length
            ? state.checkedHistoryByUserId[state.checkedHistoryByUserId.length - 1].id + 1
            : 1,
        clockInTime,
        clockOutTime: null,
        notes: null,
        createdAt: "",
        updatedAt: "",
        userId,
        date: getCurrentDateYMDformat(),
      };
    },

    setAddCheckoutForUser: (state, action: PayloadAction<{ clockOutTime: string }>) => {
      const { clockOutTime } = action.payload;
      if (state.checkedHistoryByUserId && state.checkedHistoryByUserId.length) {
        // Find the last open session and update it immutably
        state.checkedHistoryByUserId = state.checkedHistoryByUserId.map((session, idx, arr) => {
          if (idx === arr.length - 1 && session.clockOutTime === null) {
            return { ...session, clockOutTime };
          }
          return session;
        });

        state.clockOut = {
          id: state.checkedHistoryByUserId?.length
            ? state.checkedHistoryByUserId[state.checkedHistoryByUserId.length - 1].id + 1
            : 1,
          clockOutTime,
          clockInTime: state.checkedHistoryByUserId[state.checkedHistoryByUserId.length - 1].clockInTime,
          notes: null,
          createdAt: "",
          updatedAt: "",
          userId: state.checkedHistoryByUserId[state.checkedHistoryByUserId.length - 1].userId,
          date: getCurrentDateYMDformat(),
        };

        state.lastCheckedTime = {
          ...state.checkedHistoryByUserId[state.checkedHistoryByUserId.length - 1],
        };
      }
    },
  },
});

export const { setTimeAttendanceSlice } = timeAttendanceSlice.actions;

const convert12HourTo24Hour = (time12h: string): string => {
  const [time, modifier] = time12h.trim().split(" ");
  if (!time || !modifier) return "";

  let hours = Number(time.split(":")[0]);
  const minutes = Number(time.split(":")[1]);

  if (modifier.toUpperCase() === "PM" && hours < 12) {
    hours += 12;
  }
  if (modifier.toUpperCase() === "AM" && hours === 12) {
    hours = 0;
  }

  const hoursStr = hours.toString().padStart(2, "0");
  const minutesStr = (minutes ?? 0).toString().padStart(2, "0");

  return `${hoursStr}:${minutesStr}`;
};

// Updated parseScheduledTimeToISO to support 12-hour time like "9:00 AM"
const parseScheduledTimeToISO = (scheduledTime: string, dateString?: string) => {
  const date = dateString ? getCurrentDateYMDformat(dateString) : getCurrentDateYMDformat();

  // If scheduledTime contains AM/PM, convert to 24-hour format
  let time24 = scheduledTime;
  if (/\b(AM|PM)\b/i.test(scheduledTime)) {
    time24 = convert12HourTo24Hour(scheduledTime);
  }

  const dateTime = `${date} ${time24}`;
  return moment(dateTime, "YYYY-MM-DD HH:mm").toISOString();
};

export const calculateLateBy = (checkInTime: string, scheduledStartTime: string) => {
  if (!checkInTime || !scheduledStartTime) return "00:00";

  const scheduledISO = parseScheduledTimeToISO(scheduledStartTime);

  const checkIn = moment(checkInTime);
  const scheduled = moment(scheduledISO);

  if (!checkIn.isValid() || !scheduled.isValid() || checkIn.isSameOrBefore(scheduled)) {
    return "00:00";
  }

  const diffString = getTimeDifference(checkInTime, scheduledISO);

  if (
    diffString === "in a year" ||
    diffString === "in a month" ||
    diffString === "in a week" ||
    diffString === "in a day"
  ) {
    return "00:00";
  }

  const diffMinutes = checkIn.diff(scheduled, "minutes");
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  const hoursStr = hours.toString().padStart(2, "0");
  const minutesStr = minutes.toString().padStart(2, "0");

  return `${hoursStr}:${minutesStr}`;
};

export default timeAttendanceSlice.reducer;
